import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import {
  PREVIEW_MESSAGES,
  isPreviewErrorMessage,
  isPreviewReadyMessage,
  type PreviewApplyMessage,
  type PreviewCommand,
  type PreviewCommandMessage,
} from '@game-platform/pack-animation';

export type PreviewPayload = Omit<PreviewApplyMessage, 'type'>;

export interface AnimationPreviewHandle {
  send(command: PreviewCommand): void;
}

interface Props {
  iframeOrigin: string;
  payload: PreviewPayload;
  onError?: (message: string) => void;
}

/**
 * The iframe app's `/animation-preview` route, driven over postMessage. The
 * payload is posted on every change and again on iframe `load` and on the
 * child's READY — the same belt-and-braces `ConstructorPreview` uses, plus the
 * child asking, so no edit is lost to a listener that was not up yet.
 */
export const AnimationPreview = forwardRef<AnimationPreviewHandle, Props>(function AnimationPreview(
  { iframeOrigin, payload, onError },
  ref,
) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const post = useCallback((message: PreviewApplyMessage | PreviewCommandMessage) => {
    iframeRef.current?.contentWindow?.postMessage(message, '*');
  }, []);

  const postApply = useCallback(() => {
    post({ type: PREVIEW_MESSAGES.APPLY, ...payloadRef.current });
  }, [post]);

  useEffect(() => {
    postApply();
  }, [payload, postApply]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data: unknown = event.data;
      if (isPreviewReadyMessage(data)) postApply();
      else if (isPreviewErrorMessage(data)) onError?.(data.message);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onError, postApply]);

  useImperativeHandle(
    ref,
    () => ({
      send: (command) => post({ type: PREVIEW_MESSAGES.COMMAND, command }),
    }),
    [post],
  );

  return (
    <iframe
      ref={iframeRef}
      title="Pack opening preview"
      src={`${iframeOrigin}/animation-preview`}
      onLoad={postApply}
      // The preview scores the ceremony with sound; the gesture that starts
      // it (Play) happens out here, so the frame is handed our activation
      allow="autoplay"
      className="h-full w-full border-0"
    />
  );
});
